import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useState,
  ReactElement,
} from "react";
import { TAuthContext, TCredentials, TUser } from "../types/context";
import {
  decodeUser,
  getCredentials,
  removeCredentials,
  setCredentials,
} from "../utils/helpers";

const AuthContext = createContext<TAuthContext>({
  loggedIn: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  updateUserNumber: () => {},
  user: null,
  gettingAuth: true,
  tranToken: "",
  userNumber: "",
  authToken: "",
});

const AuthProvider = ({ children }: { children: ReactElement }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<TUser | null>(null);
  const [authToken, setAuthToken] = useState<string>("");
  const [tranToken, setTranToken] = useState<string>("");
  const [userNumber, setUserNumber] = useState<string>("");
  const [gettingAuth, setGettingAuth] = useState(true);

  useEffect(() => {
    const fetchCredentials = () => {
      const res = getCredentials();
      if (res) {
        setLoggedIn(true);
        setUser(decodeUser(res.access));
        setAuthToken(res.access);
        const storedTranToken = res.tran || res.user?.tran_token || "";
        const storedUserNumber = res.user_number || res.user?.user_number || "";
        setTranToken(storedTranToken);
        setUserNumber(storedUserNumber);
      }
      setGettingAuth(false);
    };

    fetchCredentials();
  }, []);

  const login = (credentials: TCredentials) => {
    setCredentials(credentials);
    setUser(decodeUser(credentials.access));
    setAuthToken(credentials.access);
    const loginTranToken =
      credentials.tran || credentials.user?.tran_token || "";
    setTranToken(loginTranToken);

    const loginUserNumber =
      credentials.user_number || credentials.user?.user_number || "";
    setUserNumber(loginUserNumber);

    setLoggedIn(true);
  };

  const updateUserNumber = (newNumber: string) => {
    setUserNumber(newNumber);
    setCredentials({
      access: authToken,
      user: user,
      tran: tranToken,
      user_number: newNumber,
    });
  };

  const logout = () => {
    removeCredentials();
    setUser(null);
    setLoggedIn(false);
  };

  const updateUser = (user: TUser) => {
    setCredentials({
      access: authToken,
      user,
      tran: tranToken,
      user_number: userNumber,
    });
    setUser(user);
  };

  const renderBody = (ele: ReactElement) => cloneElement(ele);

  return (
    <AuthContext.Provider
      value={{
        login,
        loggedIn: loggedIn,
        logout,
        user,
        gettingAuth,
        authToken,
        tranToken,
        userNumber,
        updateUserNumber,
        updateUser,
      }}
    >
      {renderBody(children)}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const LogoutRoute = () => <>{useAuth().logout()}</>;

export { AuthProvider, useAuth, LogoutRoute };
